import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Accordion,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  FormGroup,
  FormLabel,
  Image,
  InputGroup,
  ListGroupItem,
  Modal,
  Row,
} from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as yup from 'yup'
import {
  EmailShareButton,
  FacebookShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'react-share'

import { IRegistration } from '../../../store/registrations/interfaces'
import { updateRegistration } from '../../../store/registrations/registrations'
import { RootState } from '../../../store'
import { AddValidatorFunc } from '../../../hooks/validators'
import { addToast } from '../../../store/misc/misc'
import { ToastType } from '../../../store/misc/interfaces'
import { useAppDispatch } from '../../../hooks'

import groupCodeImage from '../../../staticfiles/images/group_code_example.png'

const fetchGroupcode = async (baseUrl: string) => {
  console.log('Fetching group code...')
  try {
    const response = await fetch(
      `${baseUrl}/api/form/generate/friendgroupcodes/`,
    )
    return (await response.json()) as string
  } catch (error: unknown) {
    return undefined
  }
}

const copyToClipboard = (str: string) => {
  navigator.clipboard.writeText(str).then(() => {
    /* clipboard successfully set */
  }, () => {
    /* clipboard write failed */
    const el = document.createElement('textarea')
    el.value = str
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
  })
}

const ShareButtons = (props: { code: string; registration: IRegistration }) => {
  const { data: program } = useSelector((state: RootState) => state.program)
  const { code, registration } = props
  const { baseUrl, programId } = useSelector((state: RootState) => state.misc)
  const programUrl = `${baseUrl}/${programId}/`

  const shareText = `Ich habe mich für die Veranstaltung "${registration.event.name}" in ${program.name} angemeldet. Wenn du meiner Gruppe beitreten willst, nutze ${code} als Gruppencode! Folge einfach dem Link um dich anzumelden!`

  return (
    <Dropdown.Menu>
      <Dropdown.Item onClick={() => copyToClipboard(code)}>
        <Row>
          <Col className='d-flex justify-content-between'>
            <FontAwesomeIcon icon='clipboard' />
            <span className='m-0 p-0'>Kopieren</span>
          </Col>
        </Row>
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item>
        <FontAwesomeIcon icon='envelope' />
        <EmailShareButton url={programUrl} body={shareText}>
          <p className='m-0'>Email</p>
        </EmailShareButton>
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item>
        <FontAwesomeIcon icon={['fab', 'facebook']} />

        <FacebookShareButton url={programUrl} quote={shareText}>
          <p className='m-0'>Facebook</p>
        </FacebookShareButton>
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item>
        <FontAwesomeIcon icon={['fab', 'telegram']} />

        <TelegramShareButton url={programUrl} title={shareText}>
          <p className='m-0'>Telegram</p>
        </TelegramShareButton>
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item>
        <FontAwesomeIcon icon={['fab', 'twitter']} />

        <TwitterShareButton url={programUrl} title={shareText}>
          <p className='m-0'>Twitter</p>
        </TwitterShareButton>
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item>
        <FontAwesomeIcon icon={['fab', 'whatsapp']} />

        <WhatsappShareButton url={programUrl} title={shareText}>
          <p className='m-0'>Whatsapp</p>
        </WhatsappShareButton>
      </Dropdown.Item>
    </Dropdown.Menu>
  )
}

const GroupCode = (props: {
  registration: IRegistration
  editable: boolean
  setError: (prevState: boolean) => void
  addValidator?: AddValidatorFunc
}) => {
  const { registration, editable, setError, addValidator } = props
  const dispatch = useAppDispatch()
  const { baseUrl } = useSelector((state: RootState) => state.misc)
  const [show, setShow] = useState(false)
  const {
    register,
    trigger,
    getValues,
    setValue,
    formState: { errors },
    watch,
  } = useForm<{
    groupCode: string
  }>({
    mode: 'onChange',
    defaultValues: {
      groupCode: registration.groupCode,
    },
    resolver: yupResolver(yup.object().shape({
      groupCode: yup.string(),
    })),
  })
  const [open, setOpen] = useState(false)
  const { data: institution } = useSelector(
    (state: RootState) => state.institution,
  )
  let groupImageUrl = `${institution.static_url}participant_app/images/group_code_example.png`
  if (institution.static_url === '/static/') {
    groupImageUrl = `${baseUrl}${institution.static_url}participant_app/images/group_code_example.png`
  }
  const lastError = useRef(errors.groupCode)
  const { loadedViaCode } = useSelector((state: RootState) => state.misc)
  const { originalRegistrationIds } = useSelector(
    (state: RootState) => state.participant.data,
  )

  const isOriginalReg = originalRegistrationIds?.includes(
    registration.event.event_id,
  )

  useEffect(
    () => {
      if ((lastError.current = errors.groupCode)) {
        if (editable && addValidator) {
          addValidator(`groupCode-reg-${registration.event.event_id}`, { trigger, formName: 'GroupCode' })
        }
      } else {
        if (errors.groupCode) {
          setError(true)
          lastError.current = errors.groupCode
        } else {
          setError(false)
          lastError.current = errors.groupCode
        }
      }
    },
    [register, errors.groupCode])

  return (
    <>
      <ListGroupItem className='m-0'>
        <FormGroup>
          <FormLabel className='d-flex justify-content-between'>
            Freundesgruppen{' '}
            <Button
              variant='outline-dark'
              size='sm'
              onClick={() => setShow(true)}
            >
              <FontAwesomeIcon icon='info-circle' role='presentation' /> Anleitung
            </Button>
          </FormLabel>
          {editable ? (
            <>
              <InputGroup className='mb-3'>
                <Form.Control
                  {...register('groupCode', {
                      onBlur: () => {
                        //registration.groupCode = getValues("groupCode")
                        dispatch(
                          updateRegistration({
                              eventId: registration.event.event_id,
                              ...getValues(),
                            },
                          ),
                        )
                      },
                    },
                  )}

                  id={`groupCode-${registration.event.event_id}`}
                  type='text'
                  disabled={!editable || (loadedViaCode && isOriginalReg)}
                  isInvalid={!!errors.groupCode}
                  isValid={!!registration.groupCode}
                  defaultValue={registration.groupCode}
                  aria-label='Gruppencode für diese Veranstaltung'
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.groupCode && errors.groupCode.message}
                </Form.Control.Feedback>
              </InputGroup>
              <Row>
                <Col md={6}>
                  <Button
                    variant='outline-primary'
                    className='btn-block'
                    disabled={loadedViaCode && isOriginalReg}
                    block
                    onClick={async () => {
                      const groupCode = await fetchGroupcode(baseUrl)
                      if (groupCode !== undefined) {
                        setValue('groupCode', groupCode)
                        dispatch(
                          updateRegistration({
                            eventId: registration.event.event_id,
                            ...getValues(),
                          }),
                        )
                      } else {
                        dispatch(
                          addToast({
                            type: ToastType.Danger,
                            title: 'Fehler',
                            body: 'Es konnte kein Gruppencode erstellt werden.',
                          }),
                        )
                      }
                    }}
                  >
                    Generieren
                  </Button>
                </Col>
                <Col md={6}>
                  <Dropdown>
                    <Dropdown.Toggle
                      variant='outline-dark'
                      className='btn-block'
                      id={`dropdown-button-${registration.event.event_id}`}
                      disabled={watch('groupCode') === ''}
                    >
                      <FontAwesomeIcon icon='share' role='presentation' />
                      &nbsp;Teilen
                    </Dropdown.Toggle>
                    <ShareButtons
                      code={registration.groupCode}
                      registration={registration}
                    />
                  </Dropdown>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <br />
              {registration.groupCode !== '' ? (
                <small className='font-weight-bold'>
                  {registration.groupCode}
                </small>
              ) : (
                <small className='text-muted'>Keine Angabe.</small>
              )}
            </>
          )}
        </FormGroup>
      </ListGroupItem>
      <Modal show={show} onHide={() => setShow(false)} size='lg'>
        <Modal.Header closeButton closeLabel={'Modal schließen'}>
          <Modal.Title>Wie funktionieren Freundesgruppen?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Image
            src={groupCodeImage}
            rounded
            className='w-100'
            alt='Erklärung des Gruppencodes'
          />
        </Modal.Body>
        <Modal.Footer>
          <Accordion className='w-100 d-flex justify-content-center flex-column'>
            <Accordion.Toggle
              className='d-flex justify-content-center btn btn-info'
              eventKey='0'
              aria-expanded={open}
              as={Button}
              variant='button'
              onClick={() => setOpen(!open)}
            >
              <>Detaillierte Erklärung des Gruppencodes</>
            </Accordion.Toggle>

            <Accordion.Collapse eventKey='0'>
              <Card.Body>
                <p>
                  In Feripro können Freundesgruppen durch einen Freundesgruppencode erkannt werden. Alle Personen,
                  die zu einer Freundesgruppe gehören, müssen bei der Anmeldung denselben Code angeben. So kann das
                  System erkennen, dass sie zusammengehören, und dies bei der Zuteilung zu einer
                  Veranstaltung berücksichtigen.
                  <br/><br/>
                  Den Code können Sie selbst erstellen. Er kann aus beliebigen Buchstaben und Zahlen bestehen.
                  Alternativ können Sie mit dem Knopf &quot;Code generieren&quot; einen zufälligen Code erstellen lassen.
                  Diesen Code können Sie dann an Ihre Freunde weitergeben, damit sie ihn bei ihrer Anmeldung eingeben.
                </p>
                <strong>Hinweise:</strong>
                <ul>
                  <li>Bitte beachten Sie Groß- und Kleinschreibung!</li>
                  <li>Ein Code gilt nur für <strong>eine</strong> Veranstaltung!</li>
                  <li>
                    Es kann nicht garantiert werden, dass die Personen gemeinsam den Veranstaltungen zugeteilt werden.
                  </li>
                </ul>
              </Card.Body>
            </Accordion.Collapse>
          </Accordion>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default GroupCode
